/**
 * SigningThanks — customer-facing terminal page after SignNow signing.
 *
 * Q9 (Bill 2026-05-10) — basic SignNow Brand wired into Phase 0. SignNow
 * redirects the customer to `https://retail.mypcb.com/signing-thanks?cartId=…`
 * (and optionally `&name=…`) when signing completes. This page is the
 * customer's last touchpoint with PCB on the signing path.
 *
 * Constraints:
 *   - No interactive elements (this is terminal — no "go back to cart").
 *   - Mobile-first (most remote signers are on phones).
 *   - PCB-branded — primary #2968ad, dark #1a406a, Roboto-system stack.
 *   - NO auth, NO Function calls — customers reach this from a SignNow link
 *     and never had a Teams session to begin with. Pure static content.
 *   - NO CDN dependencies (global rule). System fonts only.
 *
 * Routing note: wired in `App.tsx` BEFORE the auth gate so unauthenticated
 * customers can hit it directly.
 */

import type { CSSProperties } from 'react';

const containerS: CSSProperties = {
  minHeight: '100vh',
  background: 'var(--surface-bg)',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', 'Helvetica Neue', sans-serif",
  color: 'var(--ink-900)',
};

const headerS: CSSProperties = {
  background: 'var(--topbar-solid)',
  color: 'var(--topbar-fg)',
  padding: '20px 24px',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
};

const logoMarkS: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 8,
  background: 'var(--surface-card)',
  color: 'var(--pcb-blue-dark)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: 1,
  flexShrink: 0,
};

const headerTitleS: CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  lineHeight: 1.2,
};

const headerSubS: CSSProperties = {
  fontSize: 13,
  opacity: 0.85,
  marginTop: 2,
};

const mainS: CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 20px',
};

const cardS: CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: 12,
  boxShadow: '0 6px 24px rgba(15, 23, 42, 0.06)',
  maxWidth: 520,
  width: '100%',
  padding: '36px 28px',
  textAlign: 'center',
};

const checkCircleS: CSSProperties = {
  width: 72,
  height: 72,
  borderRadius: '50%',
  background: 'var(--ok-bg)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 18px',
};

const thanksH1S: CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: 24,
  fontWeight: 600,
  color: 'var(--pcb-blue-dark)',
  lineHeight: 1.25,
};

const thanksLeadS: CSSProperties = {
  margin: '0 0 16px 0',
  fontSize: 15,
  lineHeight: 1.55,
  color: 'var(--ink-900)',
};

const thanksFollowupS: CSSProperties = {
  margin: '0 0 24px 0',
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--ink-700)',
};

const divider: CSSProperties = {
  height: 1,
  background: 'var(--ink-200)',
  margin: '24px 0',
};

const backLinkS: CSSProperties = {
  display: 'inline-block',
  color: 'var(--pcb-blue)',
  fontSize: 13,
  textDecoration: 'underline',
  fontWeight: 500,
};

const footerS: CSSProperties = {
  background: 'transparent',
  padding: '14px 24px 24px',
  textAlign: 'center',
  fontSize: 11,
  color: 'var(--ink-600)',
  lineHeight: 1.6,
};

function readQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(name);
    if (!raw) return null;
    // Plain-language sanitation: strip control chars, cap length.
    return raw.replace(/[^\p{L}\p{N}\s.,'-]/gu, '').slice(0, 80) || null;
  } catch {
    return null;
  }
}

export function SigningThanks(): JSX.Element {
  const customerName = readQueryParam('name');
  const greeting = customerName ? `Thank you, ${customerName}` : 'Thank you';

  return (
    <div style={containerS} role="main" aria-label="Signing complete">
      <header style={headerS}>
        <span aria-hidden style={logoMarkS}>PCB</span>
        <div>
          <div style={headerTitleS}>Phelps County Bank</div>
          <div style={headerSubS}>Account opening</div>
        </div>
      </header>

      <main style={mainS}>
        <section style={cardS}>
          <div style={checkCircleS} aria-hidden>
            {/* Inline SVG checkmark — no external icon dep, no CDN. */}
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--ok-text)"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="4 12 10 18 20 6" />
            </svg>
          </div>

          <h1 style={thanksH1S}>{greeting} — your documents have been signed.</h1>

          <p style={thanksLeadS}>
            We&apos;ve received your signatures. An associate at Phelps County
            Bank has been notified and will follow up with you shortly.
          </p>

          <p style={thanksFollowupS}>
            You can safely close this window. If you have questions before we
            reach out, give us a call at{' '}
            <strong style={{ color: 'var(--pcb-blue-dark)' }}>(573) 458-3700</strong> and
            we&apos;ll be glad to help.
          </p>

          <div style={divider} aria-hidden />

          <a href="https://www.pcb.bank" style={backLinkS}>
            Back to Phelps County Bank&apos;s website
          </a>
        </section>
      </main>

      <footer style={footerS}>
        Phelps County Bank · Member FDIC · Equal Housing Lender
      </footer>
    </div>
  );
}

export default SigningThanks;
